import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

/**
 * Fetches an admin-auth image (the Marketing PNG render endpoint on the API
 * host) once, and exposes it as a Blob plus an object URL for <img>.
 *
 * A bare <img src> is not relied on: the endpoint needs the admin session, so
 * the request carries the cookie (credentials: 'include') and, while
 * impersonating, the Bearer token, the same way baseQuery and
 * utils/authedDownload do. The copy and download buttons reuse the same Blob,
 * so there is one request per image. The object URL is revoked on change and
 * unmount.
 */
export const useAuthedImageBlob = (url: string | null) => {
  const token = useSelector((state: RootState) => state.auth?.token);
  const [state, setState] = useState<{ blob: Blob | null; objectUrl: string | null; error: boolean }>({
    blob: null,
    objectUrl: null,
    error: false,
  });
  const blobPromiseRef = useRef<Promise<Blob> | null>(null);

  useEffect(() => {
    if (!url) {
      blobPromiseRef.current = null;
      setState({ blob: null, objectUrl: null, error: false });
      return undefined;
    }
    let cancelled = false;
    let objectUrl: string | null = null;

    const headers: Record<string, string> = {};
    const isImpersonating = typeof window !== 'undefined' && localStorage.getItem('isImpersonating') === 'true';
    if (token && (isImpersonating || process.env.REACT_APP_USE_COOKIE_AUTH !== 'true')) {
      headers.Authorization = `Bearer ${token}`;
    }

    const promise = fetch(url, { method: 'GET', credentials: 'include', headers }).then((res) => {
      if (!res.ok) throw new Error(`Image request failed (${res.status})`);
      return res.blob();
    });
    blobPromiseRef.current = promise;

    promise
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ blob, objectUrl, error: false });
      })
      .catch(() => {
        if (!cancelled) setState({ blob: null, objectUrl: null, error: true });
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url, token]);

  return { ...state, blobPromise: blobPromiseRef.current };
};

export default useAuthedImageBlob;
